import React, { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import EntryForm from './EntryForm';
import ImageLightbox from './ImageLightbox';
import LazyImage from './LazyImage';

const ENTRIES_PER_PAGE = 10;
const SKELETON_COUNT = 10;

function getVisiblePageItems(totalPages, currentPage) {
  if (totalPages <= 14) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const visiblePages = new Set([1, 2, 3, totalPages - 2, totalPages - 1, totalPages]);

  if (currentPage > 3 && currentPage < totalPages - 2) {
    visiblePages.add(currentPage);
  }

  const sortedPages = Array.from(visiblePages).sort((left, right) => left - right);
  const pageItems = [];

  sortedPages.forEach((pageNumber, index) => {
    if (index > 0 && pageNumber - sortedPages[index - 1] > 1) {
      pageItems.push(`ellipsis-${sortedPages[index - 1]}-${pageNumber}`);
    }

    pageItems.push(pageNumber);
  });

  return pageItems;
}

async function copyToClipboard(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = value;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
}

function EntriesList({ entries, loading = false, onDelete, onEdit }) {
  const [editingEntry, setEditingEntry] = useState(null);
  const [lightboxState, setLightboxState] = useState(null);
  const [toastState, setToastState] = useState(null);
  const [entryPendingDelete, setEntryPendingDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const listTopRef = useRef(null);

  const entriesWithImages = useMemo(
    () =>
      entries.map((entry) => ({
        ...entry,
        images: Array.isArray(entry.images) ? entry.images : [],
      })),
    [entries]
  );

  const totalPages = Math.max(1, Math.ceil(entriesWithImages.length / ENTRIES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const visiblePageItems = getVisiblePageItems(totalPages, safePage);
  const paginatedEntries = entriesWithImages.slice(
    (safePage - 1) * ENTRIES_PER_PAGE,
    safePage * ENTRIES_PER_PAGE
  );
  const entryPageSignature = entriesWithImages.map((entry) => entry.id).join('|');

  useEffect(() => {
    setCurrentPage(1);
  }, [entryPageSignature]);

  useEffect(() => {
    if (!toastState) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setToastState(null), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [toastState]);

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
      return;
    }

    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [safePage, currentPage]);

  useEffect(() => {
    setPageInput(String(safePage));
  }, [safePage]);

  const getYouTubeVideoId = (url) => {
    if (!url) return null;

    const regex =
      /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const renderSkeletons = () => (
    <div className="entries-list page-transition is-active">
      {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
        <div
          className="entry-card entry-card-skeleton"
          key={`skeleton-${index}`}
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <div className="skeleton-line wide"></div>
          <div className="skeleton-line medium"></div>
          <div className="skeleton-image"></div>
          <div className="skeleton-line wide"></div>
          <div className="skeleton-line short"></div>
        </div>
      ))}
    </div>
  );

  const showToast = (message, tone = 'success') => {
    setToastState({ message, tone });
  };

  const handleConfirmDelete = async () => {
    if (!entryPendingDelete) {
      return;
    }

    try {
      await onDelete(entryPendingDelete.id);
      showToast('Entry removed');
    } catch (error) {
      showToast('Delete failed', 'error');
    } finally {
      setEntryPendingDelete(null);
    }
  };

  const handlePageJump = (event) => {
    event.preventDefault();

    const requestedPage = Number.parseInt(pageInput, 10);
    const isValidPage =
      Number.isInteger(requestedPage) && requestedPage >= 1 && requestedPage <= totalPages;

    if (!isValidPage) {
      showToast(`Invalid page. Enter a number between 1 and ${totalPages}.`, 'error');
      setPageInput(String(safePage));
      return;
    }

    setCurrentPage(requestedPage);
  };

  if (!loading && entriesWithImages.length === 0) {
    return (
      <div className="empty-state" ref={listTopRef}>
        <div className="sparkle-container">
          <div className="sparkle">
            <div className="sparkle-blue"></div>
            <div className="sparkle-yellow"></div>
            <div className="sparkle-pink"></div>
            <div className="sparkle-center"></div>
          </div>
        </div>
        <div className="empty-state-text">Your sanctum is empty</div>
        <div className="empty-state-subtext">Add your first item to begin</div>
      </div>
    );
  }

  return (
    <>
      <div ref={listTopRef}></div>

      {loading ? (
        renderSkeletons()
      ) : (
        <>
          <div className="entries-list page-transition is-active">
            {paginatedEntries.map((entry, index) => (
              <div
                className="entry-card stagger-in"
                key={entry.id}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="entry-header">
                  <div className="entry-type-container">
                    <div className={`entry-type ${entry.displayType || entry.type}`}>
                      {entry.type === 'note' && (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="entry-type-icon"
                            width="16"
                            height="16"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                          Note
                        </>
                      )}

                      {entry.type === 'link' && (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="entry-type-icon"
                            width="16"
                            height="16"
                          >
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                          Link
                        </>
                      )}

                      {entry.type === 'image' && (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="entry-type-icon"
                            width="16"
                            height="16"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                          </svg>
                          Image
                        </>
                      )}

                      {entry.type === 'video' && (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="entry-type-icon"
                            width="16"
                            height="16"
                          >
                            <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
                            <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" />
                          </svg>
                          YouTube
                        </>
                      )}
                    </div>
                    <h3 className="entry-title">{entry.title}</h3>
                  </div>

                  <div className="entry-actions">
                    <button
                      type="button"
                      className="entry-action-btn"
                      onClick={() => setEditingEntry(entry)}
                      aria-label="Edit"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        width="16"
                        height="16"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      className="entry-action-btn"
                      onClick={() => setEntryPendingDelete(entry)}
                      aria-label="Delete"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        width="16"
                        height="16"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>

                {(entry.type === 'note' || entry.type === 'image') && entry.body && (
                  <div className="entry-content">{entry.body}</div>
                )}

                {entry.type === 'link' && entry.body && (
                  <a
                    href={entry.body}
                    className="entry-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {entry.body}
                  </a>
                )}

                {entry.type === 'video' && entry.body && (
                  <div className="youtube-preview">
                    {getYouTubeVideoId(entry.body) ? (
                      <div className="youtube-player">
                        <iframe
                          src={`https://www.youtube.com/embed/${getYouTubeVideoId(entry.body)}`}
                          title={entry.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <div className="entry-youtube-thumbnail">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          width="36"
                          height="36"
                        >
                          <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                          <line x1="7" y1="2" x2="7" y2="22" />
                          <line x1="17" y1="2" x2="17" y2="22" />
                          <line x1="2" y1="12" x2="22" y2="12" />
                          <line x1="2" y1="7" x2="7" y2="7" />
                          <line x1="2" y1="17" x2="7" y2="17" />
                          <line x1="17" y1="17" x2="22" y2="17" />
                          <line x1="17" y1="7" x2="22" y2="7" />
                        </svg>
                        <div className="invalid-url-notice">Invalid YouTube URL</div>
                      </div>
                    )}
                    <a
                      href={entry.body}
                      className="entry-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {entry.body}
                    </a>
                  </div>
                )}

                {entry.images.length > 0 && (
                  <div className="entry-gallery" aria-label={`Image gallery for ${entry.title}`}>
                    {entry.images.map((image, imageIndex) => (
                      <div key={image.id} className="entry-gallery-item">
                        <LazyImage
                          src={image.previewSrc || image.src}
                          fallbackSrc={image.src}
                          alt={image.alt || image.name || `${entry.title} image ${imageIndex + 1}`}
                          className="entry-gallery-image"
                          wrapperClassName="entry-gallery-image-shell"
                          ariaLabel={`Open image ${imageIndex + 1} in fullscreen`}
                          onClick={() =>
                            setLightboxState({ images: entry.images, index: imageIndex })
                          }
                        />
                        <button
                          type="button"
                          className="image-copy-btn"
                          aria-label={`Copy image URL for ${image.alt || image.name || `image ${imageIndex + 1}`}`}
                          onClick={async () => {
                            try {
                              await copyToClipboard(image.src);
                              showToast('Copied!');
                            } catch (error) {
                              showToast('Copy failed', 'error');
                            }
                          }}
                        >
                          Copy URL
                        </button>
                        <a
                          href={image.src}
                          className="image-visit-btn"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open image URL for ${image.alt || image.name || `image ${imageIndex + 1}`}`}
                        >
                          Visit URL
                        </a>
                        {image.alt && <div className="entry-image-alt">{image.alt}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {entry.tags && entry.tags.length > 0 && (
                  <div className="entry-tags">
                    {entry.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="entry-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="pagination-bar">
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={safePage === 1}
            >
              Previous
            </button>

            <div className="pagination-pages">
              {visiblePageItems.map((item) => {
                if (typeof item !== 'number') {
                  return (
                    <span key={item} className="pagination-ellipsis" aria-hidden="true">
                      ...
                    </span>
                  );
                }

                return (
                  <button
                    type="button"
                    key={item}
                    className={`pagination-page ${item === safePage ? 'is-active' : ''}`}
                    onClick={() => setCurrentPage(item)}
                    aria-label={`Go to page ${item}`}
                    aria-current={item === safePage ? 'page' : undefined}
                  >
                    {item}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={safePage === totalPages}
            >
              Next
            </button>
          </div>

          <div className="pagination-counter">
            Page {safePage} of {totalPages}
          </div>

          <form className="pagination-jump" onSubmit={handlePageJump}>
            <label htmlFor="pagination-page-input" className="pagination-jump-label">
              Jump to page
            </label>
            <div className="pagination-jump-controls">
              <input
                id="pagination-page-input"
                type="number"
                min="1"
                max={totalPages}
                inputMode="numeric"
                className="pagination-jump-input"
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                aria-label={`Enter a page number between 1 and ${totalPages}`}
              />
              <button type="submit" className="pagination-btn pagination-jump-btn">
                Go
              </button>
            </div>
          </form>
        </>
      )}

      {editingEntry && (
        <EntryForm
          initialData={editingEntry}
          initialType={editingEntry.displayType || editingEntry.type}
          onSubmit={async (updatedEntry) => {
            await onEdit(updatedEntry);
            setEditingEntry(null);
            showToast('Entry updated');
          }}
          onCancel={() => setEditingEntry(null)}
        />
      )}

      {entryPendingDelete && (
        <ConfirmDialog
          title="Delete entry?"
          message={`"${entryPendingDelete.title}" will be removed from your archive.`}
          confirmLabel="Delete"
          cancelLabel="Keep"
          confirmVariant="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => setEntryPendingDelete(null)}
        />
      )}

      {lightboxState && (
        <ImageLightbox
          images={lightboxState.images}
          activeIndex={lightboxState.index}
          onClose={() => setLightboxState(null)}
          onNavigate={(direction) => {
            setLightboxState((currentState) => {
              if (!currentState) return currentState;

              const nextIndex =
                (currentState.index + direction + currentState.images.length) %
                currentState.images.length;

              return { ...currentState, index: nextIndex };
            });
          }}
        />
      )}

      {toastState && (
        <div className={`copy-toast ${toastState.tone === 'error' ? 'error' : 'success'}`} role="status" aria-live="polite">
          {toastState.message}
        </div>
      )}
    </>
  );
}

export default EntriesList;
